import { Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { CHART_VIEWS, type Zoom } from "./chart-utils";

/**
 * One segmented control for how far the chart runs and, in the "When it happens" view, the
 * period size that goes with it: "2027 · months", "2030 · quarters", "2035 · years" (PRD §9, A16).
 * `fit` adds a "Fit" option (metrics: just the entered dates), with value "fit".
 */
export function ChartViewControl({
	value,
	onChange,
	bars = false,
	fit = false,
}: {
	/** The view end (grid index), or "fit". */
	value: number | "fit";
	onChange: (viewEnd: number | "fit", zoom: Zoom) => void;
	/** Label the options with their period size ("When it happens" view). */
	bars?: boolean;
	fit?: boolean;
}) {
	return (
		<ToggleGroup
			type="single"
			size="sm"
			variant="outline"
			spacing={0}
			aria-label="Chart range"
			value={String(value)}
			onValueChange={(v) => {
				if (!v) return;
				if (v === "fit") return onChange("fit", "year");
				const view = CHART_VIEWS.find((c) => String(c.t) === v)!;
				onChange(view.t, view.zoom);
			}}
		>
			{fit && (
				<ToggleGroupItem value="fit" title="Just the entered dates">
					Fit
				</ToggleGroupItem>
			)}
			{CHART_VIEWS.map((v) => (
				<ToggleGroupItem key={v.t} value={String(v.t)} title={bars ? `To ${v.year}, by ${v.zoomLabel.slice(0, -1)}` : `To ${v.year}`}>
					{bars ? (
						<>
							{v.year} <span className="opacity-60">· {v.zoomLabel}</span>
						</>
					) : (
						`to ${v.year}`
					)}
				</ToggleGroupItem>
			))}
		</ToggleGroup>
	);
}

/** "Keep total" for the bars view: dragging a bar moves chance between periods. */
export function KeepTotalSwitch({ checked, onCheckedChange }: { checked: boolean; onCheckedChange: (v: boolean) => void }) {
	return (
		<label
			className="flex h-7 cursor-pointer items-center gap-2 text-xs font-medium text-foreground"
			title="Move chance between periods; the overall chance stays the same"
		>
			<Switch size="sm" checked={checked} onCheckedChange={onCheckedChange} /> Keep total
		</label>
	);
}

/** Opens the full-screen editor (or the full-screen view, for read-only charts). */
export function OpenEditorButton({ onOpen, readOnly }: { onOpen: () => void; readOnly?: boolean }) {
	return (
		<Button
			type="button"
			variant="outline"
			size="sm"
			onClick={onOpen}
			onKeyDown={(e) => e.stopPropagation()}
			title={readOnly ? "Open full screen" : "Open the editor, with the table"}
		>
			<Maximize2 /> {readOnly ? "Full screen" : "Open editor"}
		</Button>
	);
}

export interface LegendItem {
	c: string;
	l: string;
	dash?: boolean;
	band?: boolean;
}

/** Legend row for under a chart. */
export function ChartLegend({ items }: { items: LegendItem[] }) {
	return (
		<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
			{items.map((it) => (
				<span key={it.l} className="inline-flex items-center gap-1.5">
					{it.band ? (
						<span className="inline-block h-2.5 w-4 rounded-sm" style={{ background: it.c, opacity: 0.25 }} />
					) : (
						<svg width="18" height="6" aria-hidden>
							<line x1="0" x2="18" y1="3" y2="3" stroke={it.c} strokeWidth="2.5" strokeDasharray={it.dash ? "4 3" : undefined} />
						</svg>
					)}
					{it.l}
				</span>
			))}
		</div>
	);
}
